import { environment } from "@/environments/environment";
import { HttpClient, HttpErrorResponse, HttpHeaders } from "@angular/common/http";
import { Injectable, signal, WritableSignal } from "@angular/core";
import { map, catchError, throwError, Observable, BehaviorSubject } from "rxjs";
import { AppConfig } from "../shared/app-config";
import { ErrorDataService } from "./error-data.service";
import { EventTypeModel, SaveTemplatePriorityRequest, TemplateModel, TemplatePriorityLog, TemplatePriorityModel } from "../model/template.priority.model";
import { DropdownEntry } from "../model/dropdown-entry";
import { Product } from "../model/product";

@Injectable({
  providedIn: "root",
})
export class TemplatesPriorityService {

  // Dispatch if there is a loading in progress
  private isLoadingActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // Dispatch if save button is enabled
  private isSaveEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  // Dispatch what actions has been done (EDIT, CANCEL, CLEAR_ALL, SAVE, SAVE_OK)
  private action: BehaviorSubject<string> = new BehaviorSubject<string>('');
  // Dispatch error messages
  private error: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  // Dispatch the OPPs list
  private opps: BehaviorSubject<Product[]> = new BehaviorSubject<Product[]>([]);
  // Dispatch the selected entries in the dropdown
  private dropdownSelectedEntries: BehaviorSubject<DropdownEntry[]> = new BehaviorSubject<DropdownEntry[]>([]);
  // Dispatch the default template priorities
  private defaultTemplatePriorities: BehaviorSubject<TemplatePriorityModel[]> = new BehaviorSubject<TemplatePriorityModel[]>([]);
  // Dispatch the template priorities (freezed state)
  private templatePriorities: BehaviorSubject<TemplatePriorityModel[]> = new BehaviorSubject<TemplatePriorityModel[]>([]);
  // Dispatch the edited template priorities (that must be saved)
  private editedTemplatePriorities: BehaviorSubject<TemplatePriorityModel[]> = new BehaviorSubject<TemplatePriorityModel[]>([]);
  //Data to log in splunk
  public loggedTemplatePriorities: WritableSignal<TemplatePriorityLog[]> = signal<TemplatePriorityLog[]>([]);

  constructor(private httpClient: HttpClient) { }

  public setIsLoadingActive(value: boolean): void {
    this.isLoadingActive.next(value);
  }

  public getIsLoadingActive(): BehaviorSubject<boolean> {
    return this.isLoadingActive;
  }

  public setIsSaveEnabled(value: boolean): void {
    this.isSaveEnabled.next(value);
  }

  public getIsSaveEnabled(): BehaviorSubject<boolean> {
    return this.isSaveEnabled;
  }

  public setAction(value: string): void {
    this.action.next(value);
  }

  public getAction(): BehaviorSubject<string> {
    return this.action;
  }

  public setError(value: any): void {
    this.error.next(value);
  }

  public getError(): BehaviorSubject<any> {
    return this.error;
  }

  public setOpps(value: Product[]): void {
    this.opps.next(value);
  }

  public getOpps(): BehaviorSubject<Product[]> {
    return this.opps;
  }

  public setDropdownSelectedEntries(value: DropdownEntry[]): void {
    this.dropdownSelectedEntries.next(value);
  }

  public getDropdownSelectedEntries(): BehaviorSubject<DropdownEntry[]> {
    return this.dropdownSelectedEntries;
  }

  public setDefaultTemplatePriorities(value: TemplatePriorityModel[]): void {
    this.defaultTemplatePriorities.next(value);
  }

  public getDefaultTemplatePriorities(): BehaviorSubject<TemplatePriorityModel[]> {
    return this.defaultTemplatePriorities;
  }

  public setTemplatePriorities(value: TemplatePriorityModel[]): void {
    this.templatePriorities.next(value);
  }

  public getTemplatePriorities(): BehaviorSubject<TemplatePriorityModel[]> {
    return this.templatePriorities;
  }

  public setEditedTemplatePriorities(value: TemplatePriorityModel[]): void {
    this.editedTemplatePriorities.next(value);
  }

  public getEditedTemplatePriorities(): BehaviorSubject<TemplatePriorityModel[]> {
    return this.editedTemplatePriorities;
  }

  public getTemplatesWithByEventTypesEnabled(): string[] {
    return ['CURTAIL', 'FOLLOW_UP_CONFIRM', 'RESTORE'];
  }

  // HTTP - GET Template Priorities
  retrieveTemplatePriorities(eventTypes: String[], templates: TemplateModel[], product_id?: string): Observable<TemplatePriorityModel[]> {
    const body = {
      event_types: eventTypes,
      template_names: templates.map((template) => template.name),
      product_id: product_id == 'ALL' ? null : product_id
    }
    let headers: HttpHeaders = new HttpHeaders();
    headers = headers.append("Content-Type", "application/json");
    headers = headers.append("Accept", "application/json");
    return this.httpClient
      //CHANGE URL WHEN API IS READY
      .post(
        environment.contentLibraryApiBaseUrl + AppConfig.urlPriorities,
        body,
        {
          headers: headers,
        }
      )
      .pipe(
        map((response) => {
          const templatePriorities = [];
          response["data"].forEach((res: any) => {
            let templatePriority: TemplatePriorityModel = new TemplatePriorityModel(
              res.template_name,
              templates.find((template) => template.name === res.template_name).label,
              res.event_types,
              res.priority,
              [],
              []
            );
            templatePriorities.push(templatePriority);
          });
          return templatePriorities.sort((a: TemplatePriorityModel, b: TemplatePriorityModel) => {
            const nameA = a.template_name;
            const nameB = b.template_name;
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          });
        }),
        catchError((err: Error | HttpErrorResponse) => {
          const message = `No Template Priority Found.`;
          return throwError(ErrorDataService.overLoadError(err, message));
        })
      );
  }

  // HTTP - POST Save Template Priority
  saveTemplatePriority(template_name: string, event_type: string, priority: string, product_id?: string): Observable<TemplatePriorityModel[]> {
    if (priority) {
      priority = priority.toUpperCase();
    }
    return this.httpClient.post(environment.contentLibraryApiBaseUrl + AppConfig.urlPriority, { template_name, event_type, priority, product_id }).pipe(map(
      response => {
        if (response['code'] === 200) {
          return response['message'];
        }
      }
    ), catchError((err: Error | HttpErrorResponse) => {
      const message = `Error in TemplatePriorityService saveTemplatePriority`;
      return throwError(ErrorDataService.overLoadError(err, message));
    }));
  }

  // HTTP - POST Save Template Priorities (Bulk API)
  saveTemplatePriorities(templatepriorities: Array<SaveTemplatePriorityRequest>, logged_info?: Array<TemplatePriorityLog>): Observable<TemplatePriorityModel[]> {
    templatepriorities.forEach((templatepriority) => {
      if (templatepriority.priority) {
        templatepriority.priority = templatepriority.priority.toUpperCase();
      }
    });
    const data = {
      templatepriorities: templatepriorities,
      logged_info: logged_info,
    }
    return this.httpClient.post(environment.contentLibraryApiBaseUrl + AppConfig.urlPrioritiesBulk, data ).pipe(map(
      response => {
        if (response['code'] === 200) {
          return response['message'];
        }
      }
    ), catchError((err: Error | HttpErrorResponse) => {
      const message = `Error in TemplatePriorityService saveTemplatePriorities`;
      return throwError(ErrorDataService.overLoadError(err, message));
    }));
  }

  // HTTP - GET Event Types
  retrieveEventTypes(): Observable<EventTypeModel[]> {
    return this.httpClient
      //CHANGE URL WHEN API IS READY
      .get(environment.contentLibraryApiBaseUrl + AppConfig.urlEvents)
      .pipe(
        map((response) => {
          const eventTypes = [];
          response['data'].forEach((res: any) => {
            eventTypes.push(new EventTypeModel(res.code, res.label));
          });
          return eventTypes;
        }),
        catchError((err: Error | HttpErrorResponse) => {
          const message = `No Event Types Found.`;
          return throwError(ErrorDataService.overLoadError(err, message));
        })
      );
  }

  // HTTP - GET Template Names
  retrieveTemplatesNames(): Observable<TemplateModel[]> {
    return this.httpClient
      //CHANGE URL WHEN API IS READY
      .get(environment.contentLibraryApiBaseUrl + AppConfig.urlTemplates)
      .pipe(
        map((response) => {
          const templates = [];
          response['data'].forEach((res: any) => {
            templates.push(new TemplateModel(res.template_name, res.display_label));
          });
          return templates;
        }),
        catchError((err: Error | HttpErrorResponse) => {
          const message = `No Templates Found.`;
          return throwError(ErrorDataService.overLoadError(err, message));
        })
      );
  }
}